import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Col,
  Card,
  Button,
  Watermark,
  Tabs,
} from "antd";
import {
  RollbackOutlined,
  SaveOutlined,
  SendOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { SearchSelectDebounce } from "Components/SearchSelectDebounce/SearchSelectDebounce";
import { ChooseWorks } from "Pages/MyProjectPage/components/CalendarWrapper/components/ChooseWorks/ChooseWorks";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  getAllMaterialsProjectToSelectTelegram,
  getAllUsersToSelectTelegram,
  getAllWorksToSelectTelegram,
} from "Api/TelegramApi/helpers";
import { getTelegram } from "../../Api/TelegramApi/telegram";
import { errorNotification } from "Components/MyNotification";
import TelegramApi from "Api/TelegramApi/TelegramApi";
import { MyTimePicker } from "Components/MyTimePicker/MyTimePicker";
import MyBtnConfirm from "Components/MyBtnConfirm/MyBtnConfirm";
import { defaultSettings } from "css/defaultAntStyles";
import { MyLoader } from "Components/MyLoader/MyLoader";
import "./mystyles.css";

const { TextArea } = Input;

export const TelegramBlankPage = () => {
  const tg = getTelegram();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [workDate, setDate] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedWorks, setSelectedWorks] = useState([]);
  const [blankMaterials, setBlankMaterials] = useState([]);

  const isCanSendData = useMemo(() => {
    if (
      !startTime ||
      !endTime ||
      !description ||
      !workDate ||
      !users.length ||
      !selectedWorks.length ||
      !params.projectId
    ) {
      return false;
    }

    const checkIsValueOfWorksIsNotWrited = selectedWorks.some(
      (el) => el.value === 0,
    );
    const checkIsValueOfMaterialsIsNotWrited = blankMaterials.length
      ? blankMaterials.some((el) => el.value === 0)
      : false;

    return (
      !checkIsValueOfWorksIsNotWrited && !checkIsValueOfMaterialsIsNotWrited
    );
  }, [
    startTime,
    endTime,
    description,
    workDate,
    users,
    selectedWorks,
    blankMaterials,
    params.projectId,
    tg,
  ]);
  const onSendData = async () => {
    if (isCanSendData) {
      try {
        setIsLoading(true);
        const result = await TelegramApi.createBlank({
          blankId: searchParams.get("blankId"),
          startTime,
          endTime,
          description,
          workDate,
          users,
          blankMaterials,
          selectedWorks,
          projectId: params.projectId,
          fromTelegram: true,
        });
        if (result.status === "fail") {
          errorNotification(result.message);
          return;
        }

        tg.close();
      } catch (err) {
        errorNotification(err?.message || "Помилка при створенні бланку");
      } finally {
        setIsLoading(false);
      }
    }
  };
  const onSaveData = async () => {
    try {
      setIsLoading(true);
      const result = await TelegramApi.saveBlank({
        blankId: searchParams.get("blankId"),
        startTime,
        endTime,
        description,
        workDate,
        users,
        selectedWorks,
        blankMaterials,
        projectId: params.projectId,
        fromTelegram: true,
      });
      if (result.status === "fail") {
        errorNotification(result.message);
        return;
      }
      tg.close();
    } catch (err) {
      errorNotification(err?.message || "Помилка при збереженні бланку");
    } finally {
      setIsLoading(false);
    }
  };
  const getSingleSavedBlank = async (blankId) => {
    try {
      setIsLoading(true);
      const result = await TelegramApi.getSingleBlank(
        params.projectId,
        blankId,
      );
      if (result.status === "fail") {
        errorNotification(result.message);
        return;
      }
      const {
        startTime,
        endTime,
        description,
        workDate,
        users,
        selectedWorks,
        blankMaterials,
      } = result.dto;

      setStartTime(startTime);
      setEndTime(endTime);
      setDescription(description);
      setDate(workDate);
      setUsers(users);
      setSelectedWorks(selectedWorks);
      setBlankMaterials(blankMaterials);
    } catch (err) {
      errorNotification(err?.message || "Помилка при отриманні бланку");
      tg.close();
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const tg = getTelegram();

    const isTelegram =
      tg && tg.initData && tg.initData.length > 0 && tg.platform !== "unknown";

    if (!isTelegram || !params.projectId) {
      setIsAllowed(false);
      return;
    }

    setIsAllowed(true);

    tg.ready();
    tg.expand();
    if (searchParams.get("blankId")) {
      const blankId = searchParams.get("blankId");
      getSingleSavedBlank(blankId);
    }
  }, []);

  useEffect(() => {
    const worksToInsert = [...blankMaterials]
      .filter((el) => el.workTypeTied)
      .map((el) => el.workTypeTied)
      .filter((el) => {
        const isSaveElem = [...selectedWorks].some(
          (elem) => elem.allowedWorkTypeId === el.allowedWorkTypeId,
        );
        return !isSaveElem;
      });
    if (!worksToInsert.length) return;
    setSelectedWorks((prev) => {
      return [...prev, ...worksToInsert];
    });
  }, [blankMaterials]);

  useEffect(() => {
    const materialsFromWorksArray = [];
    const materialsToInsert = [...selectedWorks]
      .filter((el) => {
        if (el.materialsForWork && el.materialsForWork.length) {
          return true;
        }
      })
      .forEach((el) =>
        el.materialsForWork.forEach((elem) => {
          materialsFromWorksArray.push(elem);
        }),
      );

    const materialsToInsertToBlankMaterials = materialsFromWorksArray.filter(
      (el) => {
        const isSaveMaterial = [...blankMaterials].some(
          (elem) => elem.id === el.id,
        );
        return !isSaveMaterial;
      },
    );
    if (!materialsToInsertToBlankMaterials.length) return;
    setBlankMaterials((prev) => {
      return [...prev, ...materialsToInsertToBlankMaterials];
    });
  }, [selectedWorks]);

  const setChangeValue = (data) => {
    const { allowedWorkTypeId, value } = data;
    setSelectedWorks((prev) => {
      return [...prev].map((el) => {
        if (el.allowedWorkTypeId === allowedWorkTypeId) {
          el.value = +value;
        }
        return el;
      });
    });
  };
  const setChangeMaterials = (data) => {
    const { id, value } = data;
    setBlankMaterials((prev) => {
      return [...prev].reduce((acc, el) => {
        if (el.id === id) {
          if (el.restCount >= value) {
            el.value = +value;
            el.restCountAfterChanges = +el.restCount;
            el.restCountAfterChanges = +el.restCount - +value;
          } else {
            errorNotification("Не можна ввести кількість більше залишку");
          }
        }
        acc.push(el);
        return acc;
      }, []);
    });
  };

  const setCommentData = (dataValues) => {
    const { param, comment, typeContent } = dataValues;
    // param: item.allowedWorkTypeId || item.id,
    // comment: inputValue,
    // typeContent: 'material' || 'work',
    switch (typeContent) {
      case "material":
        setBlankMaterials((prev) => {
          return [...prev].map((elem) => {
            if (elem.id === param) {
              elem.comment = comment;
            }
            return elem;
          });
        });

        break;
      case "work":
        setSelectedWorks((prev) => {
          return [...prev].map((elem) => {
            if (elem.allowedWorkTypeId === param) {
              elem.comment = comment;
            }
            return elem;
          });
        });

        break;
    }
  };

  if (isAllowed === false) {
    return (
      <Watermark content="ПРОМАВТОМАТИКА" rotate={30} gap={[90, 90]}>
        <Card
          bodyStyle={{ padding: "30px 8px 10px 8px", height: "100vh" }}
        ></Card>
      </Watermark>
    );
  }
  if (isLoading) {
    return <MyLoader isFull />;
  }
  return (
    <Card bodyStyle={{ padding: "25px 8px 10px 8px", minHeight: "100vh" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "right",
          gap: "10px",
          alignItems: "center",
        }}
      >
        {searchParams.get("blankId") && (
          <Button
            icon={<RollbackOutlined style={{ fontSize: "20px" }} />}
            onClick={() =>
              navigate(`/telegram/blank/${params.projectId}/saved`)
            }
          />
        )}
        <MyBtnConfirm
          icon={
            <SaveOutlined style={{ color: defaultSettings.colorPrimary }} />
          }
          btnIcon={<SaveOutlined />}
          isLoading={isLoading}
          type=""
          mainTitle="Зберегти бланк?"
          fontSize={"20px"}
          onConfirm={onSaveData}
        />
      </div>
      <Form layout="vertical">
        <Col span={24}>
          <Col span={24}>
            <Form.Item label="Час виконання робіт">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                }}
              >
                <MyTimePicker
                  size="large"
                  placeholder={"Старт робіт"}
                  onChange={(time) =>
                    // dispatch(setWorkTime({ param: "startTime", time }))
                    setStartTime(time)
                  }
                  value={startTime}
                />
                <MyTimePicker
                  size="large"
                  placeholder={"Кінець робіт"}
                  onChange={(time) =>
                    // dispatch(setWorkTime({ param: "endTime", time }))
                    setEndTime(time)
                  }
                  value={endTime}
                />
              </div>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Дата виконання">
              <DatePicker
                size="large"
                style={{ width: "100%" }}
                format="DD.MM.YYYY"
                placeholder="Оберіть дату"
                value={workDate ? dayjs(workDate) : null}
                onChange={(e) => setDate(e)}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Виберіть працівників що виконували роботу">
              <SearchSelectDebounce
                prefix={<TeamOutlined />}
                size="large"
                onChange={(e) => {
                  const userIds = (Array.isArray(e) ? e : [])
                    .filter(Boolean)
                    .map((el) => el?.value ?? el)
                    .filter(Boolean);
                  const usersData = userIds.map((user) => {
                    const json = user.split("___")[0];
                    const raw = JSON.parse(json);
                    return raw;
                  });
                  // dispatch(setUsers(usersData));
                  setUsers(usersData);
                }}
                customFetch={getAllUsersToSelectTelegram}
                mode="multiple"
                value={users.map((el) => {
                  return {
                    value: JSON.stringify(el) + `___` + el.id,
                    label: el.name,
                  };
                })}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Опис робіт">
              <TextArea
                rows={4}
                placeholder="Опишіть виконані роботи"
                value={description}
                // onChange={(e) => dispatch(setDescription(e.target.value))}
                onChange={(e) => setDescription(e.target.value)}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Tabs
              className="my-custom-materials-tabs"
              defaultActiveKey="1"
              items={[
                {
                  label: `Виберіть роботи`,
                  key: 1,
                  children: (
                    <ChooseWorks
                      title=""
                      projectId={params.projectId}
                      fetchFunction={getAllWorksToSelectTelegram}
                      onChange={(worksData) => {
                        const dataToSet = worksData.reduce((acc, el) => {
                          const isInArr = acc.some(
                            (elem) =>
                              elem.allowedWorkTypeId === el.allowedWorkTypeId,
                          );
                          if (!isInArr) {
                            acc.push(el);
                          }
                          return acc;
                        }, []);
                        setSelectedWorks(dataToSet);
                      }}
                      changeValue={(dataValues) => setChangeValue(dataValues)}
                      changeComment={(commentData) => {
                        setCommentData(commentData);
                      }}
                      selectedWorks={selectedWorks}
                      size="large"
                    />
                  ),
                },
                {
                  label: `Матеріали`,
                  key: 2,
                  children: (
                    <ChooseWorks
                      title=""
                      findMaterials={true}
                      projectId={params.projectId}
                      fetchFunction={getAllMaterialsProjectToSelectTelegram}
                      onChange={(materialsData) => {
                        // const dataToSet = materialsData.reduce((acc, el) => {
                        //   const isInArr = acc.some((elem) => elem.id === el.id);
                        //   if (!isInArr) {
                        //     acc.push(el);
                        //   }
                        //   return acc;
                        // }, []);
                        setBlankMaterials(materialsData);
                      }}
                      changeValue={(value) => setChangeMaterials(value)}
                      changeComment={(commentData) => {
                        setCommentData(commentData);
                      }}
                      selectedWorks={blankMaterials}
                      size="large"
                      placeholder="Пошук матеріалів..."
                    />
                  ),
                },
              ]}
            />
          </Col>
          {/* <Col span={24}>
              <Form.Item label="Виберіть роботи">
                <ChooseWorks
                  title=""
                  projectId={params.projectId}
                  fetchFunction={getAllWorksToSelectTelegram}
                  onChange={(worksData) => {
                    const dataToSet = worksData.reduce((acc, el) => {
                      const isInArr = acc.some(
                        (elem) =>
                          elem.allowedWorkTypeId === el.allowedWorkTypeId,
                      );
                      if (!isInArr) {
                        acc.push(el);
                      }
                      return acc;
                    }, []);
                    setSelectedWorks(dataToSet);
                  }}
                  changeValue={(value) => setChangeValue(value)}
                  selectedWorks={selectedWorks}
                  size="large"
                />
              </Form.Item>
            </Col> */}

          <div
            style={{
              margin: "20px 0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {isCanSendData && (
              <MyBtnConfirm
                icon={
                  <SendOutlined
                    style={{ color: defaultSettings.colorPrimary }}
                  />
                }
                textBtn="Відправити бланк"
                type="primary"
                disabled={!isCanSendData}
                isLoading={isLoading}
                onConfirm={onSendData}
                mainTitle="Відправити бланк на перевірку?"
                fontSize="18px"
              />
              // <Button
              //   size="large"
              //   onClick={onSendData}
              //   type="primary"
              //   disabled={!isCanSendData}
              //   loading={isLoading}
              // >
              //   Відправити бланк
              // </Button>
            )}
          </div>
        </Col>
      </Form>
    </Card>
  );
};
